import {
  UpdateTaskRequest,
  WeightProofVerificationControllerService
} from "@9amhealth/openapi";
import {
  NineActions,
  NineButton,
  NineHeading
} from "@9amhealth/wcl/generated/react";
import styled from "@emotion/styled";
import { Blac } from "blac-next";
import type { FC } from "react";
import React, { useRef, useState } from "react";
import translate from "src/lib/translate";
import FileType from "src/constants/fileType";
import { KnownGroup, KnownProgram } from "src/constants/programs";
import { tracker } from "src/state/state";
import TaskManagementBloc, {
  TaskKey
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import AppDialog from "src/ui/components/AppDialog/AppDialog";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import InDialog from "src/ui/components/InDialog/InDialog";
import Translate from "src/ui/components/Translate/Translate";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import FileUploadDocument from "../../FileUploadDocument/FileUploadDocument";

const Container = styled.div`
  max-width: 600px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  padding: var(--space-md);
  text-align: center;
`;

const StepContent = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SmallText = styled.p`
  color: var(--color-c-80);
  margin: 0;
  margin-top: auto;
  text-align: center;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--Accessible-Blue, #3973da);
  cursor: pointer;
  font-weight: 500;
  padding: 0;

  &:hover {
    color: var(--color-primary-dark);
  }
`;

const WeightProofUploadDialog: FC<{ returnUrl: string }> = ({ returnUrl }) => {
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [uploadReady, setUploadReady] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const navigate = useGoToOrBack();
  const actionsRef = useRef<HTMLDivElement>(null);

  function onClose() {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  }

  const handleSkip = () => {
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    tracker.track("Weight Proof Upload Skipped");
    void taskManagementState
      .updateTaskStatus(
        {
          program: KnownProgram.ONBOARDING,
          group: KnownGroup.GLP1_CONTINUATION,
          slug: TaskKey.UPLOAD_PROOF_OF_WEIGHT
        },
        UpdateTaskRequest.status.SKIPPED
      )
      .then(() => {
        AppQueryPopupsController.closePopup();
      });
  };

  const handleContinue = () => {
    BlockingLoadingOverlayController.startLoading({
      title: "weightProofUpload.verify.loading",
      bg: "transparent"
    });

    void WeightProofVerificationControllerService.verify().then((result) => {
      if (result.data.verified) {
        BlockingLoadingOverlayController.loadingSuccess({
          message: "weightProofUpload.verify.success"
        });
      } else {
        BlockingLoadingOverlayController.loadingError({
          title: null,
          showIcon: true,
          message: result.data.reasonIfNotVerified ?? undefined,
          cancelText: "weightProofUpload.verify.error.backToUpload",
          onCancel: () => BlockingLoadingOverlayController.endLoading(),
          onSkip: handleRequestSkip,
          skipText: "weightProofUpload.verify.error.skipForNow"
        });
      }
    });
  };

  const handleRequestSkip = () => {
    setShowSkipConfirm(true);
  };

  const handleSkipConfirmClose = () => {
    setShowSkipConfirm(false);
  };

  return (
    <>
      {showSkipConfirm && (
        <AppDialog
          confirmText="weightProofUpload.skipConfirm.skip"
          cancelText="weightProofUpload.skipConfirm.backToUpload"
          confirmArrow
          onCancel={handleSkipConfirmClose}
          onClose={handleSkipConfirmClose}
          onConfirm={handleSkip}
        >
          <h5>
            <Translate msg="weightProofUpload.skipConfirm.title" />
          </h5>
          <p>
            <Translate msg="weightProofUpload.skipConfirm.description" />
          </p>
        </AppDialog>
      )}
      <InDialog
        popup
        title={translate("weightProofUpload.title")}
        returnUrl={returnUrl}
        onClose={onClose}
      >
        <Container>
          <StepContent>
            <NineHeading>
              <h4 className="as-h4-large">
                <Translate msg="weightProofUpload.step.title" />
              </h4>
            </NineHeading>
            <p>
              <Translate msg="weightProofUpload.step.description" />
            </p>
            <FileUploadDocument
              onFileIdsChange={setUploadedFileIds}
              onReadyStateChange={setUploadReady}
              fileType={FileType.PRESCRIPTION_PROOF_OF_WEIGHT}
              fileTags={["weight-proof"]}
              title={null}
              description={null}
              uploadLabel={
                <Translate msg="weightProofUpload.upload.uploadLabel" />
              }
              helperText={
                <Translate msg="weightProofUpload.upload.helperText" />
              }
            />
            <div ref={actionsRef}>
              {uploadedFileIds.length > 0 && (
                <NineActions style={{ justifyContent: "center" }}>
                  <NineButton
                    disabled={!uploadReady ? "true" : "false"}
                    onClick={handleContinue}
                  >
                    <Translate msg="weightProofUpload.upload.sendAndComplete" />
                  </NineButton>
                </NineActions>
              )}
            </div>
            <SmallText>
              <Translate msg="weightProofUpload.missingDocumentation" />
              <br />
              <LinkButton onClick={handleRequestSkip}>
                <Translate msg="weightProofUpload.skipForNow" />
              </LinkButton>
            </SmallText>
          </StepContent>
        </Container>
      </InDialog>
    </>
  );
};

export default WeightProofUploadDialog;
